import React, { useState, useEffect } from 'react';
import axios from 'axios';
import CustomSelect from '../widgets/CustomSelect';
import CodeInput from '../widgets/CodeInput';
import '../../styles/MedicationOrder.css';

const MedicationOrder = ({ order, onClose, onUpdate }) => {
  const [schedule, setSchedule] = useState([]);
  const [nurseId, setNurseId] = useState('');
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [warning, setWarning] = useState('');
  const [loading, setLoading] = useState(false);

  // Initialize schedule from order
  useEffect(() => {
    const initialSchedule = order.schedule || [];
    if (initialSchedule.length > 0) {
      // Sort the schedule in ascending order for display
      const sortedSchedule = [...initialSchedule].sort((a, b) => {
        const aMinutes = timeToMinutes(a);
        const bMinutes = timeToMinutes(b);
        return aMinutes - bMinutes;
      });
      setSchedule(sortedSchedule);
    } else {
      setSchedule(initialSchedule);
    }
  }, [order]);

  const validateTimeFormat = (time) => {
    return /^([01]\d|2[0-3]):(00|30)$/.test(time);
  };

  // Convert time string to minutes for easier comparison
  const timeToMinutes = (timeStr) => {
    const [hours, minutes] = timeStr.split(':').map(Number);
    return hours * 60 + minutes;
  };

  // Check if times are in sequential order (allowing wrap-around)
  const areTimesSequential = (times) => {
    if (times.length <= 2) return true; // No validation needed for 1 or 2 doses
    
    const timeMinutes = times.map(timeToMinutes);
    console.log('Time minutes:', timeMinutes);
    
    // Check if times are already in ascending order
    let isAscending = true;
    for (let i = 1; i < timeMinutes.length; i++) {
      if (timeMinutes[i] <= timeMinutes[i - 1]) {
        isAscending = false;
        break;
      }
    }
    console.log('Is ascending:', isAscending);
    if (isAscending) return true;
    
    // For non-ascending sequences, check if it's a valid wrap-around
    // A valid wrap-around has exactly one "break" where a smaller time follows a larger time
    // and represents crossing midnight
    
    let breakCount = 0;
    let breakIndex = -1;
    
    for (let i = 1; i < timeMinutes.length; i++) {
      if (timeMinutes[i] < timeMinutes[i - 1]) {
        breakCount++;
        breakIndex = i;
      }
    }
    
    // Must have exactly one break for valid wrap-around
    if (breakCount !== 1) return false;
    
    // Check that times before break are in ascending order
    for (let i = 1; i < breakIndex; i++) {
      if (timeMinutes[i] <= timeMinutes[i - 1]) return false;
    }
    
    // Check that times after break are in ascending order
    for (let i = breakIndex + 1; i < timeMinutes.length; i++) {
      if (timeMinutes[i] <= timeMinutes[i - 1]) return false;
    }
    
    // For valid wrap-around, the last time before midnight should be later in the day
    // than the first time after midnight, and all times after break should be earlier
    // than all times before break (representing next day)
    const beforeBreak = timeMinutes.slice(0, breakIndex);
    const afterBreak = timeMinutes.slice(breakIndex);
    
    // All times after break should be smaller than all times before break
    const maxAfterBreak = Math.max(...afterBreak);
    const minBeforeBreak = Math.min(...beforeBreak);
    
    return maxAfterBreak < minBeforeBreak;
  };

  const handleTimeChange = (index, newTime) => {
    const formattedTime = newTime;
    if (!validateTimeFormat(formattedTime)) {
      return;
    }

    const updatedSchedule = [...schedule];
    updatedSchedule[index] = formattedTime;
    
    // Validate the entire sequence
    if (!areTimesSequential(updatedSchedule)) {
      setWarning('Times must be in sequential order. Example: 08:00, 14:00, 20:00 or 22:00, 02:00, 08:00');
      return;
    }
    
    setWarning('');
    setSchedule(updatedSchedule);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    // Validate all times and sequence
    if (!schedule.every(validateTimeFormat)) {
      setError('All times must be in HH:00 or HH:30 format');
      return;
    }
    
    if (!areTimesSequential(schedule)) {
      setError('Times must be in sequential order');
      return;
    }

    setLoading(true);

    try {
      // Verify PIN
      const verifyResponse = await axios.post(
        `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/employees/verify-code`,
        { employee_id: nurseId, security_code: pin }
      );

      if (verifyResponse.data.result === 'Approved') {
        // Update the medication order
        const response = await axios.put(
          `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/admissions/medication-orders/${order.order_id}`,
          {
            schedule,
            updatedBy: nurseId
          }
        );

        onUpdate(response.data);
        onClose();
      } else {
        setError('Invalid PIN');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update schedule');
    } finally {
      setLoading(false);
    }
  };
  const slots = Array.from({length: 24*2}, (_, i) => {
    const h = String(Math.floor(i/2)).padStart(2,'0');
    const m = i%2===0 ? '00' : '30';
    return `${h}:${m}`;
  });

  return (
    <div className="medication-order-modal">
      <div className="modal-content">
        <h3>Edit Medication Schedule</h3>
        <p>
          <strong>{order.medication_name}</strong> {order.dosage} ({order.route}) - {order.frequency}
        </p>

        <form onSubmit={handleSubmit}>
          <div className="schedule-times">
            <div className="schedule-header">Medication Times</div>
            {warning && <div className="warning-message">{warning}</div>}
            {schedule.map((time, index) => (
              <div key={index} className="time-input">
                <label>Dose {index + 1}</label>
                <>
                <input
                  type="time"
                  value={time.slice(0, 5)} // Remove seconds for input
                  onChange={(e) => handleTimeChange(index, e.target.value)}
                  step="1800" // Force 30-minute intervals
                  list="slots"
                  required
                  lang="en"
                />
                <datalist id="slots">
                  {slots.map(s => <option key={s} value={s} />)}
                </datalist>
                </>
              </div>
            ))}
          </div>

          <div className="form-group">
            <label>Nurse</label>
            <CustomSelect
              name="nurseId"
              value={nurseId}
              onChange={(_, value) => setNurseId(value)}
              endpoint="/hospital-portal/api/employees/byrole?role=Nurse"
              valueField="employee_id"
              labelField="employee_name"
              isSearchable
            />
          </div>

          <div className="form-group">
            <label>Enter PIN</label>
            <CodeInput
              length={4}
              value={pin}
              onChange={setPin}
            />
          </div>

          {error && <div className="error-message">{error}</div>}

          <div className="modal-actions">
            <button type="button" onClick={onClose}>Cancel</button>
            <button type="submit" disabled={loading || !nurseId || pin.length !== 4}>
              {loading ? 'Updating...' : 'Update Schedule'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MedicationOrder;
